import type { BadgeVariant } from '@/components/ui/badge';
import type { JsonFilter } from '@/hooks/use-table-filter/types';
import { parseAsRFCSafeString } from '@/hooks/use-table-filter/utils';
import { Urls } from '@/lib/urls';
import { ObjectValues } from '@/lib/utils';
import {
  getDateRange,
  type DateCategory,
  type DateRange,
} from '@/requests/hooks/utils';
import type { ModelName } from '@pigello/pigello-matrix';
import { DateTime } from 'luxon';
import type { Statistic, Widget } from './types';

const modelNameToUrlMap = (
  modelName: ModelName,
  statistic?: Statistic,
  isNonRentalInvoice?: boolean
): string | null => {
  const statisticIdentifier = statistic?.identifier;

  const map: Partial<Record<ModelName, string>> = {
    apartmentcontract: Urls.dashboard.contracts.apartment,
    apartment: Urls.dashboard.apartments,
    industrialpremises: Urls.dashboard.industrialPremises,
    outdoorsection: Urls.dashboard.outdoorSections,
    parkingspot: Urls.dashboard.parkingSpots,
    commonarea: Urls.dashboard.commonAreas,
    brfapartment: Urls.dashboard.brf.brfApartments,
    industrialpremisescontract: Urls.dashboard.contracts.industrialPremises,
    parkingspotcontract: Urls.dashboard.contracts.parkingSpot,
    outdoorsectioncontract: Urls.dashboard.contracts.outdoorSection,
    othercontract: Urls.dashboard.contracts.otherContracts,
    blockcontract: Urls.dashboard.contracts.blockContracts,
    invoice: isNonRentalInvoice
      ? Urls.dashboard.invoicing.nonRentalInvoices
      : Urls.dashboard.invoicing.invoices,
    invoicepayment: Urls.dashboard.invoicing.payments,
    sensor: Urls.dashboard.imd.sensors,
    organizationuser: Urls.settings.organization.users.default,
    tenant: Urls.dashboard.tenants,
    signabledocument: statisticIdentifier?.includes('apartment_contracts')
      ? Urls.dashboard.contracts.signingProcesses('apartmentcontract')
      : statisticIdentifier?.includes('industrialpremises_contracts')
        ? Urls.dashboard.contracts.signingProcesses(
            'industrialpremisescontract'
          )
        : statisticIdentifier?.includes('outdoorsection_contracts')
          ? Urls.dashboard.contracts.signingProcesses('outdoorsectioncontract')
          : statisticIdentifier?.includes('parkingspot_contracts')
            ? Urls.dashboard.contracts.signingProcesses('parkingspotcontract')
            : statisticIdentifier?.includes('other_contracts')
              ? Urls.dashboard.contracts.signingProcesses('othercontract')
              : statisticIdentifier?.includes('block_contracts')
                ? Urls.dashboard.contracts.signingProcesses('blockcontract')
                : undefined,
    automaticdistributionquota: Urls.dashboard.revenue.distribution.default,
    verificationgroup: Urls.dashboard.invoicing.verificationGroups,
    verification: Urls.dashboard.invoicing.verifications,
    notificationdistribution: Urls.dashboard.invoicing.failedDelivieries,
    coinvoicenotation: Urls.dashboard.invoicing.failedMerges,
    generalerrand: Urls.dashboard.technicalmanagement.errands,
    inspectionerrand: Urls.dashboard.technicalmanagement.inspections,
  };

  const url = map[modelName];

  if (!url) return null;

  return url;
};

export function getGreeting() {
  const now = DateTime.now();
  const hour = now.hour;
  if (hour >= 0 && hour <= 4) {
    return 'God natt';
  }
  if (hour > 4 && hour <= 11) {
    return 'God morgon';
  }
  if (hour > 11 && hour <= 18) {
    return 'God dag';
  }
  if (hour > 18 && hour <= 23) {
    return 'God kväll';
  }
  return 'God morgon';
}

export function getNonRentalInvoiceFlag(identifier: string): boolean {
  const segments = identifier.split('.');
  if (segments.length < 2) {
    console.warn(
      `Invalid identifier format: "${identifier}". Expected format: "category.subcategory"`
    );
    return false;
  }
  return segments[1] === 'non_rental_invoice';
}

export function extractIdentifiers(
  data: Partial<Record<string, Omit<Widget, 'modelNames'>[]>>
): string[] {
  return ObjectValues(data)
    .flatMap((items) =>
      (items ?? []).flatMap((item) =>
        item.statistics.map((stat) => stat.identifier)
      )
    )
    .filter((stat) => stat != null);
}
export function createUrl({
  modelName,
  filters,
  plainUrlReturn,
  statistic,
  isNonRentalInvoice,
}: {
  modelName: ModelName;
  filters: JsonFilter;
  plainUrlReturn?: boolean;
  statistic?: Statistic;
  isNonRentalInvoice?: boolean;
}): string {
  const url = modelNameToUrlMap(modelName, statistic, isNonRentalInvoice);

  if (!url) return '';
  if (plainUrlReturn) return url;
  return `${url}?filter=${parseAsRFCSafeString.serialize(filters)}`;
}

export const subCategoryTranslationMap: Record<
  string,
  {
    title: string;
    modelName: ModelName;
  }
> = {
  apartment_contracts: {
    title: 'Lägenhetsavtal',
    modelName: 'apartmentcontract',
  },
  industrialpremises_contracts: {
    title: 'Lokalavtal',
    modelName: 'industrialpremisescontract',
  },
  parkingspot_contracts: {
    title: 'Parkeringsavtal',
    modelName: 'parkingspotcontract',
  },
  outdoorsection_contracts: {
    title: 'Utomhussektionsavtal',
    modelName: 'outdoorsectioncontract',
  },
  other_contracts: {
    title: 'Övriga avtal',
    modelName: 'othercontract',
  },
  block_contracts: {
    title: 'Blockavtal',
    modelName: 'blockcontract',
  },
  apartment: {
    title: 'Lägenheter',
    modelName: 'apartment',
  },
  industrial_premises: {
    title: 'Lokaler',
    modelName: 'industrialpremises',
  },
  outdoor_section: {
    title: 'Utomhussektioner',
    modelName: 'outdoorsection',
  },
  common_area: {
    title: 'Gemensamma ytor',
    modelName: 'commonarea',
  },
  brf_apartment: {
    title: 'Bostadsrätter',
    modelName: 'brfapartment',
  },
  parking_spot: {
    title: 'Fordonsplatser',
    modelName: 'parkingspot',
  },
  vacancy_objects: {
    title: 'Vakanser',
    modelName: 'apartment',
  },
  invoice: {
    title: 'Avier',
    modelName: 'invoice',
  },
  non_rental_invoice: {
    title: 'Fakturor',
    modelName: 'invoice',
  },
  invoice_payment: {
    title: 'Betalningar',
    modelName: 'invoicepayment',
  },
  imd: {
    title: 'IMD',
    modelName: 'sensor',
  },
  monitoring: {
    title: 'Godkännanden',
    modelName: 'monitoringconfiguration',
  },
  brf: {
    title: 'BRF',
    modelName: 'brfapartment',
  },
  bookkeeping: {
    title: 'Bokföring',
    modelName: 'verificationgroup',
  },
  errands: {
    title: 'Ärenden',
    modelName: 'generalerrand',
  },
  inspections: {
    title: 'Besiktningar',
    modelName: 'inspectionerrand',
  },
};

export const categorizingOptions = [
  {
    label: 'Påminnelse',
    value: '#BC3C3D',
  },
];

export const widgetColors: Record<
  NonNullable<BadgeVariant['variant']>,
  string
> = {
  destructive: '#fbe2e0',
  yellow: '#fff6d2',
  blue: '#cfe7ff',
  purple: '#d8d9fa',
  orange: '#fceed8',
  cyan: '#d7f9fd',
  green: '#dff9d8',
  gray: '#888888',
  'light-gray': '#e5e5e5',
  'bright-gray': '#f1f1f1',
  default: '#002772',
  outline: '#ff000000',
};

export const reverseWidgetColors: Record<string, BadgeVariant['variant']> = {
  '#fbe2e0': 'destructive',
  '#fff6d2': 'yellow',
  '#cfe7ff': 'blue',
  '#d8d9fa': 'purple',
  '#fceed8': 'orange',
  '#d7f9fd': 'cyan',
  '#dff9d8': 'green',
  '#888888': 'gray',
  '#e5e5e5': 'light-gray',
  '#f1f1f1': 'bright-gray',
  '#002772': 'default',
  '#ff000000': 'outline',
};
// 0 = debt
// 1 = inkasso
// 2 = kronofogden
export const reminderTranslationMap: Record<number, string> = {
  [-1]: 'system_events.invoices.reminder_invoices',
  0: 'system_events.invoices.reminder_debt_invoices',
  1: 'system_events.invoices.inkasso_invoices',
};

export const specialFuckingCases: Record<string, boolean> = {
  'system_events.invoices.reminder_invoices': true,
  'system_events.invoices.reminder_debt_invoices': true,
  'system_events.invoices.inkasso_invoices': true,
} as const;

export function processTimeline(
  timeline: string[],
  range: DateRange,
  dateCategory: DateCategory,
  debtTimeLine: {
    [date: string]: {
      [debtKind: number]: number;
    };
  },
  stat: { identifier: string }
): Map<string, number> {
  const sendoutCount = new Map<string, number>();

  for (const date of timeline) {
    const sendoutTime = DateTime.fromISO(date);
    if (sendoutTime.isValid) {
      const startDate = getDateRange(range, dateCategory)?.startDate.toMillis();
      const validStartDate = !startDate || sendoutTime.toMillis() >= startDate;

      if (validStartDate) {
        const endDate = getDateRange(range, dateCategory)?.endDate.toMillis();
        const validEndDate = !endDate || sendoutTime.toMillis() <= endDate;

        if (validEndDate) {
          for (const [kind, count] of Object.entries(
            debtTimeLine[sendoutTime.toISODate()]
          )) {
            if (
              reminderTranslationMap[parseInt(kind, 10)]?.includes(
                stat.identifier
              )
            ) {
              sendoutCount.set(
                stat.identifier,
                (sendoutCount.get(stat.identifier) ?? 0) + count
              );
            }
          }
        }
      }
    }
  }

  return sendoutCount;
}
